import React, { useState } from 'react';

const MeetingBooking = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [meetingType, setMeetingType] = useState('info');
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: '',
    questions: ''
  });
  const [bookingSubmitted, setBookingSubmitted] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Available time slots
  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00'
  ];

  // Generate next 30 available dates (excluding weekends)
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    let currentDate = new Date(today);
    currentDate.setDate(today.getDate() + 1); // Start from tomorrow

    while (dates.length < 30) {
      const dayOfWeek = currentDate.getDay();
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        dates.push(new Date(currentDate));
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  const availableDates = generateAvailableDates();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    
    try {
      const meetingData = {
        ...contactInfo,
        selectedDate,
        selectedTime,
        meetingType,
        bookingDate: new Date().toISOString(),
        status: 'confirmed'
      };

      const response = await fetch(`${BACKEND_URL}/api/meetings/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(meetingData)
      });

      if (response.ok) {
        setBookingSubmitted(true);
      } else {
        alert('Failed to book meeting. Please try again.');
      }
    } catch (error) {
      console.error('Error booking meeting:', error);
      alert('Failed to book meeting. Please try again.');
    }
  };

  if (bookingSubmitted) {
    return (
      <div className="booking-success">
        <div className="success-icon">📅</div>
        <h2>Meeting Booked Successfully!</h2>
        
        <div className="booking-details">
          <h3>Meeting Details</h3>
          <div className="detail-item">
            <strong>Type:</strong> {meetingType === 'info' ? 'Information Session' : 'One-on-One Consultation'}
          </div>
          <div className="detail-item">
            <strong>Date:</strong> {new Date(selectedDate).toLocaleDateString('en-ZA', { 
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
            })}
          </div>
          <div className="detail-item">
            <strong>Time:</strong> {selectedTime}
          </div>
          <div className="detail-item">
            <strong>Format:</strong> Video call or phone consultation
          </div>
        </div>

        <div className="next-steps">
          <h3>What to Prepare</h3>
          <ul>
            <li>Valid driver's license</li>
            <li>Vehicle registration documents</li>
            <li>Insurance certificate</li>
            <li>Any questions you may have</li>
          </ul>
        </div>

        <div className="contact-reminder">
          <h3>Before Your Meeting</h3>
          <p>We'll contact you via WhatsApp or email to confirm your meeting.</p>
          <p>Need to reschedule? WhatsApp us at <strong>067 018 7407</strong></p>
        </div>

        <div className="calendar-note">
          <p>📲 <strong>We'll be in touch:</strong> Expect a confirmation message shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="meeting-booking">
      <div className="booking-header">
        <h2>Book an Information Session</h2>
        <p>Schedule a meeting to learn more about becoming a CompanionCab driver</p>
      </div>

      <div className="meeting-types">
        <h3>Choose Meeting Type</h3>
        <div className="meeting-type-options">
          <label className={`meeting-option ${meetingType === 'info' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="meetingType"
              value="info"
              checked={meetingType === 'info'}
              onChange={(e) => setMeetingType(e.target.value)}
            />
            <div className="option-content">
              <h4>📊 Information Session</h4>
              <p>Group session covering requirements, training, and earning potential</p>
              <span className="duration">Duration: 45 minutes</span>
            </div>
          </label>

          <label className={`meeting-option ${meetingType === 'consultation' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="meetingType"
              value="consultation"
              checked={meetingType === 'consultation'}
              onChange={(e) => setMeetingType(e.target.value)}
            />
            <div className="option-content">
              <h4>👤 One-on-One Consultation</h4>
              <p>Personal consultation to discuss your specific situation and questions</p>
              <span className="duration">Duration: 30 minutes</span>
            </div>
          </label>
        </div>
      </div>

      <form onSubmit={handleSubmitBooking} className="booking-form">
        <div className="form-section">
          <h3>Contact Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                name="name"
                value={contactInfo.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                name="email"
                value={contactInfo.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={contactInfo.phone}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Select Date & Time</h3>
          
          <div className="date-selection">
            <label>Preferred Date *</label>
            <select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              required
            >
              <option value="">Select a date...</option>
              {availableDates.map(date => (
                <option key={date.toISOString()} value={date.toISOString()}>
                  {date.toLocaleDateString('en-ZA', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </option>
              ))}
            </select>
          </div>

          <div className="time-selection">
            <label>Preferred Time *</label>
            <div className="time-slots">
              {timeSlots.map(time => (
                <label key={time} className={`time-slot ${selectedTime === time ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="selectedTime"
                    value={time}
                    checked={selectedTime === time}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    required
                  />
                  {time}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Questions or Special Requirements</h3>
          <div className="form-group full-width">
            <label>Any specific questions or topics you'd like to discuss?</label>
            <textarea
              name="questions"
              value={contactInfo.questions}
              onChange={handleInputChange}
              rows="4"
              placeholder="Tell us about your background, concerns, or specific questions..."
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="book-meeting-btn">
            📅 Book Meeting
          </button>
        </div>
      </form>

      <div className="booking-info">
        <h3>Meeting Information</h3>
        <div className="info-grid">
          <div className="info-item">
            <h4>What to Expect</h4>
            <ul>
              <li>Overview of CompanionCab services</li>
              <li>Driver requirements and expectations</li>
              <li>Training program details</li>
              <li>Earning potential and payment structure</li>
              <li>Q&A session</li>
            </ul>
          </div>
          <div className="info-item">
            <h4>What to Bring</h4>
            <ul>
              <li>Valid driver's license</li>
              <li>Vehicle registration (if you have a vehicle)</li>
              <li>Insurance documents</li>
              <li>List of questions</li>
              <li>Notebook for taking notes</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="contact-alternatives">
        <h3>Get in Touch</h3>
        <div className="alternatives">
          <div className="alternative-option">
            <h4>💬 WhatsApp</h4>
            <p>Message us on WhatsApp for quick responses</p>
            <a 
              href="https://wa.me/27670187407?text=Hi%20CompanionCab%2C%20I%27m%20interested%20in%20becoming%20a%20driver" 
              target="_blank" 
              rel="noopener noreferrer"
              className="contact-btn whatsapp-btn"
            >
              Chat on WhatsApp
            </a>
          </div>
          <div className="alternative-option">
            <h4>📧 Email</h4>
            <p>Send us your questions anytime</p>
            <a href="mailto:infocompcab@gmail.com" className="contact-btn email-btn">
              infocompcab@gmail.com
            </a>
          </div>
          <div className="alternative-option">
            <h4>📞 Call Us</h4>
            <p>Speak with our team directly</p>
            <a href="tel:+27670187407" className="contact-btn phone-btn">
              067 018 7407
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingBooking;